/**
 * Service for handling resume upload, management, and related job processing.
 */

import { ResumeUploadResponse, DetailedResume, ResumeError } from '../types/resume';
import { JobForYou } from '../types/jobs';
import { getAuthToken } from '../firebase/config';

// In dev the CRA proxy (package.json "proxy") forwards /api/* to the backend.
// In production REACT_APP_API_URL is set to the actual backend origin.
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const PENDING_ENABLE_MESSAGE =
  'Your account is pending admin enablement. Please contact an admin for access.';

const getErrorDetail = (error: any): string => {
  if (!error) return '';
  if (typeof error.detail === 'string') return error.detail;
  if (typeof error.message === 'string') return error.message;
  if (error.detail && typeof error.detail.message === 'string') return error.detail.message;
  return '';
};

const isPendingApprovalError = (response: Response, error: any): boolean => {
  if (response.status !== 403) return false;
  const code = String(error?.code || error?.detail?.code || '').toUpperCase();
  return code === 'USER_PENDING_ENABLE' || code === 'USER_DISABLED_BY_ADMIN' || code === 'USER_PENDING_APPROVAL';
};

const parseApiErrorMessage = (
  response: Response,
  errorText: string,
  fallback: string
): string => {
  try {
    const error: ResumeError | any = JSON.parse(errorText);
    if (isPendingApprovalError(response, error)) return PENDING_ENABLE_MESSAGE;
    return getErrorDetail(error) || fallback;
  } catch {
    if (response.status === 403 && errorText.includes('USER_PENDING_APPROVAL')) {
      return PENDING_ENABLE_MESSAGE;
    }
    return `${fallback} (status ${response.status})`;
  }
};

export type ProcessJobWindow = 'all' | 'new' | '15d' | '30d' | '60d';

export type ProcessJobsProgressEvent = {
  type: 'progress';
  phase: 'scanning' | 'saving';
  consideredCount: number;
  matchedSoFar: number;
  /** Present when backend supports incremental process-jobs */
  scanMode?: 'full' | 'incremental';
  jobWindow?: string | null;
  cutoff?: string | null;
};

export type AiTitleMatchProgressEvent = {
  type: 'progress';
  processedJobs: number;
  totalJobs: number;
  batchIndex: number;
  batchesTotal: number;
  matchedSoFar: number;
  etaSecondsApprox: number | null;
};

export type MatchJobsProgressEvent = {
  type: 'progress';
  processedJobs: number;
  totalJobs: number;
  batchIndex: number;
  batchesTotal: number;
  scoredSoFar: number;
  etaSecondsApprox: number | null;
};

async function consumeNdjsonStream<T extends Record<string, unknown>>(
  response: Response,
  onProgress?: (obj: Record<string, unknown>) => void,
): Promise<T> {
  const ct = response.headers.get('content-type') || '';
  if (!ct.includes('ndjson') || !response.body) {
    const text = await response.text();
    if (!text.trim()) {
      throw new Error('Empty response body from server');
    }
    try {
      return JSON.parse(text) as T;
    } catch {
      throw new Error('Invalid JSON response from server');
    }
  }
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let donePayload: T | null = null;

  const handleLine = (trimmed: string) => {
    if (!trimmed) return;
    let obj: Record<string, unknown>;
    try {
      obj = JSON.parse(trimmed) as Record<string, unknown>;
    } catch {
      throw new Error('Invalid JSON line in server stream');
    }
    if (obj.type === 'progress') onProgress?.(obj);
    else if (obj.type === 'error') {
      const detail =
        typeof obj.detail === 'string' ? obj.detail : JSON.stringify(obj.detail);
      throw new Error(detail || 'Request failed');
    } else if (obj.type === 'done') {
      const { type: _t, ...rest } = obj;
      donePayload = rest as T;
    }
  };

  while (true) {
    const { done, value } = await reader.read();
    if (done) {
      buffer += decoder.decode();
      break;
    }
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() ?? '';
    for (const line of lines) {
      handleLine(line.trim());
    }
  }

  if (buffer.trim()) {
    handleLine(buffer.trim());
  }

  if (!donePayload) {
    throw new Error('No completion received from server');
  }
  return donePayload;
}

class ResumeService {
  /**
   * Upload resume file to backend
   */
  async uploadResume(file: File): Promise<ResumeUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      console.log('🔐 Getting auth token...');
      const authToken = await getAuthToken();
      console.log('✅ Auth token obtained:', authToken ? 'Yes' : 'No');
      
      console.log('📤 Uploading to:', `${API_BASE_URL}/api/resumes/upload`);
      const response = await fetch(`${API_BASE_URL}/api/resumes/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
        },
        body: formData,
      });
      
      console.log('📥 Response status:', response.status, response.statusText);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Upload error response:', errorText);
        throw new Error(parseApiErrorMessage(response, errorText, 'Failed to upload resume'));
      }
      
      const result = await response.json();
      console.log('✅ Upload successful:', result);
      return result;
    } catch (error) {
      console.error('💥 Upload error:', error);
      throw new Error(`Upload failed: ${error}`);
    }
  }

  /**
   * Get all user's resumes
   */
  async getResumes(): Promise<DetailedResume[]> {
    try {
      console.log('🔐 Getting auth token for resume fetch...');
      const authToken = await getAuthToken();
      console.log('✅ Auth token obtained for fetch:', authToken ? 'Yes' : 'No');
      
      console.log('📥 Fetching from:', `${API_BASE_URL}/api/resumes/`);
      const response = await fetch(`${API_BASE_URL}/api/resumes/`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
      });
      
      console.log('📊 Fetch response status:', response.status, response.statusText);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Fetch error response:', errorText);
        throw new Error(parseApiErrorMessage(response, errorText, 'Failed to fetch resumes'));
      }
      
      const data = await response.json();
      console.log('✅ Resumes fetched:', data);
      
      // Backend now returns an array of resumes directly
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error('💥 Fetch error:', error);
      throw new Error(`Fetch failed: ${error}`);
    }
  }

  /**
   * Update a specific resume by ID
   */
  async updateResume(resumeId: string, resumeData: Partial<DetailedResume>): Promise<{ message: string }> {
    try {
      const authToken = await getAuthToken();
      const response = await fetch(`${API_BASE_URL}/api/resumes/${resumeId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(resumeData),
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Update error response:', errorText);
        throw new Error(parseApiErrorMessage(response, errorText, 'Failed to update resume'));
      }
      
      const result = await response.json();
      console.log('✅ Resume updated successfully:', result);
      return result;
    } catch (error) {
      console.error('💥 Update error:', error);
      throw new Error(`Update failed: ${error}`);
    }
  }

  /**
   * Delete a specific resume by ID
   */
  async deleteResume(resumeId: string): Promise<{ message: string }> {
    try {
      const authToken = await getAuthToken();
      const response = await fetch(`${API_BASE_URL}/api/resumes/${resumeId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(parseApiErrorMessage(response, errorText, 'Failed to delete resume'));
      }
      
      return await response.json();
    } catch (error) {
      throw new Error(`Delete failed: ${error}`);
    }
  }

  /**
   * Trigger backend job processing for a specific resume.
   *
   * Full mode replaces the filtered list; incremental mode only adds
   * genuinely new jobs (never touches existing docs or their AI scores).
   */
  async processJobsForResume(
    resumeId: string,
    allCompanies: boolean = false,
    onProgress?: (e: ProcessJobsProgressEvent) => void,
    jobWindow: ProcessJobWindow = 'all',
  ): Promise<{
    message: string;
    processedCount: number;
    newlyAdded?: number;
    consideredCount: number;
    maxConsidered: number;
    companiesWithConnections?: number;
    scanMode?: string;
    jobWindow?: string | null;
    cutoff?: string | null;
  }> {
    try {
      const authToken = await getAuthToken();
      const url = new URL(`${API_BASE_URL}/api/resumes/${resumeId}/process-jobs`);
      url.searchParams.append('allCompanies', allCompanies.toString());
      if (jobWindow && jobWindow !== 'all') {
        url.searchParams.append('jobWindow', jobWindow);
      }

      const response = await fetch(url.toString(), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${authToken}`,
          'Content-Type': 'application/json',
          Accept: 'application/x-ndjson, application/json',
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Process jobs error response:', errorText);
        throw new Error(parseApiErrorMessage(response, errorText, 'Failed to process jobs for resume'));
      }

      return await consumeNdjsonStream(response, (obj) => {
        if (obj.type === 'progress') onProgress?.(obj as unknown as ProcessJobsProgressEvent);
      });
    } catch (error) {
      console.error('💥 Process jobs error:', error);
      throw new Error(`Process jobs failed: ${error}`);
    }
  }

  /**
   * Fetch the resolved jobs for a resume's filtered-jobs list.
   */
  async getJobsForResume(resumeId: string): Promise<JobForYou[]> {
    try {
      const authToken = await getAuthToken();
      const response = await fetch(`${API_BASE_URL}/api/resumes/${resumeId}/jobs-for-you`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Get jobs-for-you error response:', errorText);
        throw new Error(parseApiErrorMessage(response, errorText, 'Failed to fetch jobs for resume'));
      }

      const raw = await response.text();
      if (!raw.trim()) {
        console.warn('jobs-for-you returned empty body; defaulting to empty list');
        return [];
      }
      let data: unknown;
      try {
        data = JSON.parse(raw);
      } catch {
        console.error('❌ Get jobs-for-you invalid JSON — size:', raw.length, 'first 500:', raw.slice(0, 500), 'last 200:', raw.slice(-200));
        throw new Error(`Server returned invalid JSON for jobs-for-you (${raw.length} bytes)`);
      }
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error('💥 Get jobs-for-you error:', error);
      throw new Error(`Get jobs-for-you failed: ${error}`);
    }
  }

  /**
   * Run AI-based title matching for all filtered jobs for a resume.
   */
  async runAiTitleMatching(
    resumeId: string,
    northAmericaOnly: boolean = false,
    onlyNewSince?: string | null,
    onProgress?: (e: AiTitleMatchProgressEvent) => void,
  ): Promise<{ message: string; matchedCount: number; total: number; processedJobs?: number }> {
    try {
      const authToken = await getAuthToken();
      const response = await fetch(`${API_BASE_URL}/api/resumes/${resumeId}/ai-title-match`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${authToken}`,
          'Content-Type': 'application/json',
          Accept: 'application/x-ndjson, application/json',
        },
        body: JSON.stringify({ northAmericaOnly, onlyNewSince: onlyNewSince || null }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ AI title match error response:', errorText);
        throw new Error(parseApiErrorMessage(response, errorText, 'Failed to run AI title matching for resume'));
      }

      return await consumeNdjsonStream(response, (obj) => {
        if (obj.type === 'progress') onProgress?.(obj as unknown as AiTitleMatchProgressEvent);
      });
    } catch (error) {
      console.error('💥 AI title match error:', error);
      throw new Error(`AI title matching failed: ${error}`);
    }
  }

  /**
   * Match jobs by scraping descriptions and scoring fit (high/medium/low).
   * Only works for Greenhouse jobs currently.
   */
  async matchJobsWithDescriptions(
    resumeId: string,
    networkWeight: number = 70,
    fitWeight: number = 30,
    searchGoogleJobs: boolean = false,
    searchAmazonJobs: boolean = false,
    recentDomainFocus: boolean = false,
    onlyNewSince?: string | null,
    onProgress?: (e: MatchJobsProgressEvent) => void,
  ): Promise<MatchJobsResult> {
    try {
      const authToken = await getAuthToken();
      const url = new URL(`${API_BASE_URL}/api/resumes/${resumeId}/match-jobs`);
      url.searchParams.append('networkWeight', networkWeight.toString());
      url.searchParams.append('fitWeight', fitWeight.toString());
      url.searchParams.append('searchGoogleJobs', searchGoogleJobs.toString());
      url.searchParams.append('searchAmazonJobs', searchAmazonJobs.toString());
      url.searchParams.append('recentDomainFocus', recentDomainFocus.toString());
      if (onlyNewSince) url.searchParams.append('onlyNewSince', onlyNewSince);

      const response = await fetch(url.toString(), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${authToken}`,
          'Content-Type': 'application/json',
          Accept: 'application/x-ndjson, application/json',
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Match jobs error response:', errorText);
        throw new Error(parseApiErrorMessage(response, errorText, 'Failed to match jobs for resume'));
      }

      return await consumeNdjsonStream(response, (obj) => {
        if (obj.type === 'progress') onProgress?.(obj as unknown as MatchJobsProgressEvent);
      });
    } catch (error) {
      console.error('💥 Match jobs error:', error);
      throw new Error(`Job matching failed: ${error}`);
    }
  }
}

export type MatchJobsResult = {
  message: string;
  matchedCount: number;
  skippedCount: number;
  total: number;
  processedNewCount?: number;
  googleJobsAdded: number;
  amazonJobsAdded: number;
  processedJobs: Array<{
    jobUid: string;
    postingTitle: string;
    sourceCompany: string;
    jobFit: 'high' | 'medium' | 'low' | null;
    confidenceScore: number | null;
  }>;
};

// Export singleton instance
export const resumeService = new ResumeService();

// Export individual functions for backward compatibility
export const uploadResume = (file: File) => resumeService.uploadResume(file);
export const getResumes = () => resumeService.getResumes();
export const updateResume = (resumeId: string, resumeData: Partial<DetailedResume>) => resumeService.updateResume(resumeId, resumeData);
export const deleteResume = (resumeId: string) => resumeService.deleteResume(resumeId);
export const processJobsForResume = (
  resumeId: string,
  allCompanies?: boolean,
  onProgress?: (e: ProcessJobsProgressEvent) => void,
  jobWindow?: ProcessJobWindow,
) => resumeService.processJobsForResume(resumeId, allCompanies, onProgress, jobWindow);
export const getJobsForResume = (resumeId: string) => resumeService.getJobsForResume(resumeId);
export const runAiTitleMatching = (
  resumeId: string,
  northAmericaOnly?: boolean,
  onlyNewSince?: string | null,
  onProgress?: (e: AiTitleMatchProgressEvent) => void,
) => resumeService.runAiTitleMatching(resumeId, northAmericaOnly ?? false, onlyNewSince, onProgress);
export const matchJobsWithDescriptions = (
  resumeId: string,
  networkWeight?: number,
  fitWeight?: number,
  searchGoogleJobs?: boolean,
  searchAmazonJobs?: boolean,
  recentDomainFocus?: boolean,
  onlyNewSince?: string | null,
  onProgress?: (e: MatchJobsProgressEvent) => void,
) =>
  resumeService.matchJobsWithDescriptions(
    resumeId,
    networkWeight,
    fitWeight,
    searchGoogleJobs ?? false,
    searchAmazonJobs ?? false,
    recentDomainFocus ?? false,
    onlyNewSince,
    onProgress,
  );